import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import type { DialCode } from "@/types/dialcode";

type Props = {
  code: string;
  phone: string;
  dialCodes: DialCode[];
  onCodeChange: (v: string) => void;
  onPhoneChange: (v: string) => void;
  error?: string;
};

const FIELD_CLASS =
  "h-14 w-full rounded-2xl border border-input bg-card px-4 text-sm font-medium text-muted-foreground placeholder:text-border";
  
export default function PhoneField({
  code,
  phone,
  dialCodes,
  onCodeChange,
  onPhoneChange,
  error,
}: Props) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-bold tracking-wide text-field border-amber-500">
        Mobile <span className="text-destructive">*</span>
      </Label>

      <div className="grid grid-cols-[110px_1fr] gap-2">
        {/* Country Code */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
  type="button"
  disabled={dialCodes.length === 0}
  className={cn(
    FIELD_CLASS,
    "flex items-center justify-between gap-1",
    "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
    error && "border-destructive ring-3 ring-destructive/20",
  )}
>
  <span>{code}</span>

  <ChevronDown
    size={16}
    className="shrink-0 text-muted-foreground"
  />
</PopoverTrigger>

          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput placeholder="Search country..." />

              <CommandList className="max-h-72">
                <CommandEmpty>No country found.</CommandEmpty>

                <CommandGroup>
                  {dialCodes.map((d) => (
                    <CommandItem
                      key={d.code}
                      value={`${d.name} ${d.dialCode}`}
                      onSelect={() => {
                        onCodeChange(d.dialCode);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex items-center gap-2 text-sm",
                        code === d.dialCode &&
                          "bg-sky-50 data-[selected=true]:bg-sky-100",
                      )}
                    >
                      <span className="w-12 shrink-0 font-medium">
                        {d.dialCode}
                      </span>

                      <span className="text-muted-foreground">{d.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Phone Number */}
        <input
          type="text"
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, ""))}
          placeholder="Enter phone number"
          maxLength={120}
          className={cn(
            FIELD_CLASS,
            "outline-none transition-colors",
            "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
            error && "border-destructive ring-3 ring-destructive/20",
          )}
        />
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
